import React, { Component } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import Table from '@cloudscape-design/components/table';
import Pagination from '@cloudscape-design/components/pagination';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import Preferences from '@cloudscape-design/components/collection-preferences';
import Alert from '@cloudscape-design/components/alert';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Modal from '@cloudscape-design/components/modal';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { configStore } from '../stores/config';
import { brandLicensesStore } from '../stores/brandLicenses';
import { brandLicensesAddModalStore } from '../stores/brandLicensesAddModal';
import { brandLicensesEditModalStore } from '../stores/brandLicensesEditModal';

class BrandLicenses extends Component {
	constructor(props) {
		super(props);
		this.state = {
			selectedItems: [],
			showDeleteModal: false,
			isDeleting: false,
		};
	}

	componentDidMount() {
		this.props.Interface.loadBrandLicenses();
	}

	handleKeyPress = (event) => {
		if (event.detail.key === 'Enter') {
			setTimeout(() => {
				this.props.Interface.loadBrandLicenses();
			}, 0);
		}
	};

	handleDeleteSelected = () => {
		if (this.state.selectedItems.length === 0) {
			this.props.Interface.addNoty('Error', 'No items selected for deletion');
			return;
		}
		this.setState({ showDeleteModal: true });
	};

	confirmDelete = async () => {
		const { selectedItems } = this.state;
		const { Interface } = this.props;

		this.setState({ isDeleting: true });

		try {
			const ids = selectedItems.map((item) => item.id);
			await Interface.request('set', {
				class: 'data',
				module: 'brandLicenses',
				method: 'delete',
			}, { id: ids });

			Interface.addNoty('Success', `${ids.length} brand license(s) deleted successfully`);
			this.setState({ selectedItems: [], showDeleteModal: false });
			Interface.loadBrandLicenses();
		} catch (error) {
			Interface.addNoty('Error', error.text || error.toString());
		} finally {
			this.setState({ isDeleting: false });
		}
	};

	render() {
		const { brandLicenses, setBrandLicenses, config, setConfig, setBrandLicensesAddModal, brandLicensesAddModal, setBrandLicensesEditModal, brandLicensesEditModal } = this.props;
		const { selectedItems, showDeleteModal, isDeleting } = this.state;

		return (
			<Container>
				{brandLicenses.loaded && brandLicenses.error.text.length ? (
					<Box margin={{ bottom: 'm' }}>
						<Alert
							type="error"
							header="Error loading data"
							dismissible
							onDismiss={() => setBrandLicenses({ ...brandLicenses, error: { text: '', code: 0, timestamp: 0 } })}
						>
							<Box variant="p">{brandLicenses.error.text}</Box>
							<hr />
							<Box variant="p">
								Request error code: {brandLicenses.error.code} | Request timestamp: {brandLicenses.error.timestamp}
							</Box>
						</Alert>
					</Box>
				) : null}

				<Modal
					visible={showDeleteModal}
					onDismiss={() => this.setState({ showDeleteModal: false })}
					header="Confirm Deletion"
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button
									variant="link"
									onClick={() => this.setState({ showDeleteModal: false })}
									disabled={isDeleting}
								>
									Close
								</Button>
								<Button
									variant="primary"
									onClick={this.confirmDelete}
									loading={isDeleting}
									disabled={isDeleting}
								>
									Apply
								</Button>
							</SpaceBetween>
						</Box>
					}
				>
					Are you sure you want to delete {selectedItems.length} selected brand license(s)? This action cannot be undone.
				</Modal>

				<Table
					stickyHeader
					trackBy="id"
					selectionType="multi"
					selectedItems={selectedItems}
					onSelectionChange={({ detail }) => this.setState({ selectedItems: detail.selectedItems })}
					loading={!brandLicenses.loaded}
					sortingColumn={{ sortingField: brandLicenses.sorting.sortingColumn.column }}
					sortingDescending={brandLicenses.sorting.isDescending}
					onSortingChange={(sorting) => {
						setBrandLicenses({
							...brandLicenses,
							sorting: {
								...brandLicenses.sorting,
								sortingColumn: {
									order: !sorting.detail.isDescending ? 'ASC' : 'DESC',
									column: sorting.detail.sortingColumn.sortingField,
								},
								isDescending: sorting.detail.isDescending,
							},
						});
						setTimeout(() => {
							this.props.Interface.loadBrandLicenses();
						}, 250);
					}}
					columnDefinitions={[
						{
							id: 'id',
							header: 'ID',
							sortingField: 'id',
							cell: (item) => (
								<Button
									variant="link"
									onClick={() => {
										const preItem = cloneDeep(item);
										const { id } = preItem;

										delete preItem.id;
										delete preItem.createdAt;

										setBrandLicensesEditModal({
											...brandLicensesEditModal,
											show: true,
											editId: id,
											formData: { ...preItem },
											jsonData: JSON.stringify(preItem, null, 2),
										});
									}}
								>
									{item.id}
								</Button>
							),
						},
						{ id: 'company', header: 'Company', sortingField: 'company', cell: (item) => item.company || '-' },
						{ id: 'domain', header: 'Domain', sortingField: 'domain', cell: (item) => item.domain || '-' },
						{ id: 'license_name', header: 'License Name', sortingField: 'license_name', cell: (item) => item.license_name || '-' },
						{ id: 'license_number', header: 'License Number', sortingField: 'license_number', cell: (item) => item.license_number || '-' },
						{ id: 'license_country', header: 'License Country', sortingField: 'license_country', cell: (item) => item.license_country || '-' },
						{ id: 'license_address', header: 'License Address', sortingField: 'license_address', cell: (item) => item.license_address || '-' },
						{ id: 'license_url', header: 'License URL', sortingField: 'license_url', cell: (item) => item.license_url || '-' },
						{ id: 'license', header: 'License', sortingField: 'license', cell: (item) => item.license || '-' },
						{ id: 'image_path', header: 'Image Path', sortingField: 'image_path', cell: (item) => item.image_path || '-' },
						{ id: 'ltd', header: 'LTD', sortingField: 'ltd', cell: (item) => item.ltd || '-' },
						{ id: 'translate', header: 'Translate', sortingField: 'translate', cell: (item) => item.translate || '-' },
						{
							id: 'createdAt',
							header: 'Created At',
							sortingField: 'createdAt',
							cell: (item) => new Date(item.createdAt).toLocaleString(),
						},
					]}
					items={brandLicenses.data}
					preferences={
						<Preferences
							cancelLabel="Cancel"
							confirmLabel="Confirm"
							pageSizePreference={{ title: 'Brand Licenses per page', options: config.brandLicenses.preferences.perPage }}
							preferences={{ pageSize: config.brandLicenses.perPage }}
							title="Preferences"
							onConfirm={(preferences) => {
								localStorage.setItem('brandLicenses.perPage', preferences.detail.pageSize);
								setConfig({ ...config, brandLicenses: { ...config.brandLicenses, perPage: preferences.detail.pageSize } });
								setTimeout(() => {
									this.props.Interface.loadBrandLicenses();
								}, 250);
							}}
						/>
					}
					pagination={
						<Pagination
							disabled={!brandLicenses.loaded}
							currentPageIndex={brandLicenses.pagination.current}
							pagesCount={brandLicenses.pagination.total}
							onChange={(page) => {
								setBrandLicenses({ ...brandLicenses, pagination: { ...brandLicenses.pagination, current: page.detail.currentPageIndex } });
								setTimeout(() => {
									this.props.Interface.loadBrandLicenses();
								}, 250);
							}}
						/>
					}
					header={
						<Header
							variant="h1"
							description="Manage brand licenses for your platform"
							counter={`(${brandLicenses.pagination.count})`}
							actions={
								<SpaceBetween direction="horizontal" size="xs">
										<Button
											variant="normal"
											iconName="remove"
											onClick={this.handleDeleteSelected}
											disabled={isDeleting || !selectedItems.length}
										>
											Delete Selected ({selectedItems.length})
										</Button>
									<Button
										iconName="add-plus"
										disabled={!brandLicenses.loaded}
										variant="primary"
										onClick={() => {
											setBrandLicensesAddModal({ ...brandLicensesAddModal, show: true });
										}}
									>
										Add Brand License
									</Button>
								</SpaceBetween>
							}
						>
							Brand Licenses
						</Header>
					}
					empty={
						<Box textAlign="center" color="inherit">
							<b>No brand licenses found</b>
							<Box padding={{ bottom: 's' }} variant="p" color="inherit">
								No brand licenses match the current criteria.
							</Box>
							<Button
								onClick={() => setBrandLicensesAddModal({ ...brandLicensesAddModal, show: true })}
							>
								Add Brand License
							</Button>
						</Box>
					}
				/>
			</Container>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const brandLicenses = useRecoilValue(brandLicensesStore);
		const setBrandLicenses = useSetRecoilState(brandLicensesStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const brandLicensesAddModal = useRecoilValue(brandLicensesAddModalStore);
		const setBrandLicensesAddModal = useSetRecoilState(brandLicensesAddModalStore);
		const brandLicensesEditModal = useRecoilValue(brandLicensesEditModalStore);
		const setBrandLicensesEditModal = useSetRecoilState(brandLicensesEditModalStore);

		return (
			<Component
				{...props}
				brandLicenses={brandLicenses}
				setBrandLicenses={setBrandLicenses}
				config={config}
				setConfig={setConfig}
				brandLicensesAddModal={brandLicensesAddModal}
				setBrandLicensesAddModal={setBrandLicensesAddModal}
				brandLicensesEditModal={brandLicensesEditModal}
				setBrandLicensesEditModal={setBrandLicensesEditModal}
			/>
		);
	};
}

export default withRecoil(BrandLicenses);